<template>
  <div style="clear: both">
    <v-card>
      <v-toolbar dense>
        <v-toolbar-title>{{ $t('Coordinator_Actions') }}</v-toolbar-title>
      </v-toolbar>
      <v-form autocomplete="off">
        <template v-if="!isChangeFullyImplemented">
          <v-row>
            <v-col cols="12">
              <void :moc="moc" role="coordinator"></void>

              <v-dialog
                v-model="revisionReasonDialog"
                v-if="displayMakeRevisions && canMakeRevision"
                persistent
                max-width="600px"
              >
                <template v-slot:activator="{ props: activatorProps }">
                  <v-btn dark v-bind="activatorProps" color="accent" class="ma-2">
                    <span style="color: white !important;">{{ $t('Make_Revisions') }}</span>
                    </v-btn>
                </template>
                <v-card>
                  <v-toolbar color="warning" dense><v-spacer></v-spacer><v-icon class="me-3" color="black">mdi-bell-alert-outline</v-icon></v-toolbar>
                  <v-card-text class="text-h6 font-weight-bold">{{ $t('Revision_Reason') }}</v-card-text>
                  <v-card-text>
                    <v-container>
                      <v-row>
                        <v-col cols="12">
                          <v-form ref="revisionReasonMo" v-model="valid" autocomplete="off">
                            <v-textarea
                              v-model="revisionReason"
                              name="revisionReason"
                              variant="underlined"
                              :rules="revisionReasonRules"
                              auto-grow
                            ></v-textarea>
                          </v-form>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" class="card-button" text @click="revisionReasonDialog = false">{{ $t('Cancel') }}</v-btn>
                    <v-btn color="primary" class="card-button" text @click="makeRevisions">{{ $t('Save_Revise') }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <v-btn color="success" :disabled="!enableMoveNext" @click="submit()" class="ma-2">{{
                moveToNextStatusButtonText
              }}</v-btn>
            </v-col>
          </v-row>
        </template>

        <template v-if="isChangeFullyImplemented">
          <closer-actions :moc="moc" role="coordinator"></closer-actions>
        </template>
      </v-form>
    </v-card>
    <toaster ref="toaster"></toaster>
    <loading-dialog ref="loadingDialog"></loading-dialog>
  </div>
</template>

<script>
import MOCService from '@/services/moc.service'
import TaskService from '@/services/task.service'
import { FormStatus } from '@/common'
import { Flags } from '@/common/flags'
import { TaskListTypes } from '@/common/types'
import { multiFilter } from '@/common/arrayFunctions'
import { EventBus } from '@/common/event-bus'
import { AdvanceType } from '@/common/systemTypes'
import CloserActions from '@/components/mocForm/CloserActions'
import Void from '@/components/mocForm/Void'
import Toaster from '@/components/Toaster'
import LoadingDialog from '@/components/mocForm/LoadingDialog'
export default {
  name: 'coordinatorActions',
  data() {
    return {
      implementText: this.$t('Confirm_Change_Is_In_Place'),
      readyToCloseText: this.moc.flags.includes(Flags.properties[Flags.RETURNTOORIGINALCONDITION].key)
        ? this.$t('Confirm_Change_Is_Fully_Implemented_Return_To_Original')
        : this.$t('Confirm_Change_Is_Fully_Implemented'),
      revisionReasonDialog: false,
      revisionReason: '',
      revisionReasonValid: false,
      valid: false,
      moveToNextStatusButtonText: '',
      moveToNextStatusButtonEnabled: false,
      isSaving: false
    }
  },
  props: {
    moc: {
      type: Object
    },
    tasks: {
      type: Array
    }
  },
  components: {
    CloserActions,
    Void,
    Toaster,
    LoadingDialog
  },
  computed: {
    isMakePermanent() {
      return (this.moc.flags || []).includes(Flags.properties[Flags.MAKEPERMANENT].key)
    },
    canMakeRevision: function () {
      if (this.isMakePermanent) {
        return false
      }
      return (this.moc.revisionHistory || []).length < 2
    },
    closedVoidReasonRules: {
      get: function () {
        return [(v) => !!v || this.$t('Rule_Required_VoidReason')]
      }
    },
    enableMoveNext: function () {
      return this.moveToNextStatusButtonEnabled
    },
    isAvailableToImplement: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value
    },
    isChangeFullyImplemented: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value
    },
    displayMakeRevisions() {
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value:
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
        case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
          return false
      }

      return true
    },
    revisionReasonRules: {
      get: function () {
        return [(v) => !!v || this.$t('Rule_Required'), (v) => (v && v.trim().length >= 1) || this.$t('Rule_Required')]
      }
    }
  },
  methods: {
    canToMoveToNextStatus: function () {
      if (this.moc) {
        if (
          this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
          (this.moc.status.value ===
            FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
            this.moc.flags.includes(Flags.properties[Flags.RETURNTOORIGINALCONDITION].key))
        ) {
          // check the status of the tasks on the form
          // get all the required before change is fully in place tasks
          let tasksRequiredBeforeChangeIsFullyInPlace = multiFilter(this.tasks, {
            taskListType: { key: `${TaskListTypes.CONFIRMCHANGEISINPLACE.toString()}` }
          })

          // get all the required before close tasks
          let tasksRequiredBeforeClose = multiFilter(this.tasks, {
            taskListType: { key: `${TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED.toString()}` }
          })

          let completeCountTasksRequiredBeforeChangeIsFullyInPlace = tasksRequiredBeforeChangeIsFullyInPlace.filter((t) => {
            return t.completed
          })
          let comppleteCountTasksRequiredBeforeClose = tasksRequiredBeforeClose.filter((t) => {
            return t.completed
          })

          let allTasksRequiredBeforeChangeIsFullyInPlaceCompleted = false
          let allTasksRequiredBeforeCloseCompleted = false

          // check if there are no tasks for required before change is fully implemented
          if (
            tasksRequiredBeforeChangeIsFullyInPlace.length === 0 ||
            completeCountTasksRequiredBeforeChangeIsFullyInPlace.length === tasksRequiredBeforeChangeIsFullyInPlace.length
          ) {
            // all task in this status are complete lets set something to reflect that
            allTasksRequiredBeforeChangeIsFullyInPlaceCompleted = true
          }

          // check if there are not tasks for required before close
          if (
            tasksRequiredBeforeClose.length === 0 ||
            comppleteCountTasksRequiredBeforeClose.length === tasksRequiredBeforeClose.length
          ) {
            // all tasks in this status are complete lets set something else to reflect that
            allTasksRequiredBeforeCloseCompleted = true
          }

          // short circuit here
          if (
            this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
            allTasksRequiredBeforeCloseCompleted
          ) {
            this.moc.canMoveToReadyToClose = true
            this.moveToNextStatusButtonText = this.readyToCloseText
            this.moveToNextStatusButtonEnabled = true
            return
          }

          if (
            this.moc.status.value ===
              FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
            allTasksRequiredBeforeCloseCompleted
          ) {
            this.moc.canMoveToReadyToClose = true
            this.moveToNextStatusButtonText = this.readyToCloseText
            this.moveToNextStatusButtonEnabled = true
            return
          }

          if (
            this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
            allTasksRequiredBeforeChangeIsFullyInPlaceCompleted &&
            allTasksRequiredBeforeCloseCompleted
          ) {
            this.moc.canMoveToImplemented = true
            this.moc.canMoveToReadyToClose = true
            this.moveToNextStatusButtonText = this.readyToCloseText
            this.moveToNextStatusButtonEnabled = true
            return
          }

          if (
            this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
            allTasksRequiredBeforeChangeIsFullyInPlaceCompleted
          ) {
            this.moc.canMoveToImplemented = true
            this.moveToNextStatusButtonEnabled = true
            this.moveToNextStatusButtonText = this.implementText
            return
          }

          let completedTasksRequiredBeforeChangeIsFullyInPlace = tasksRequiredBeforeChangeIsFullyInPlace.filter((t) => {
            return t.completed === true
          })

          let completedTasksRequiredBeforeClose = tasksRequiredBeforeClose.filter((t) => {
            return t.completed === true
          })

          // are all task complete for required before being implemented and required before change is fully in place with some open actions and
          // are we in the form status of being implemented
          if (
            this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
            tasksRequiredBeforeChangeIsFullyInPlace.length === completedTasksRequiredBeforeChangeIsFullyInPlace.length &&
            tasksRequiredBeforeClose.length === completedTasksRequiredBeforeClose.length
          ) {
            // these tasks are complete
            this.moc.canMoveToImplemented = true
            this.moc.canMoveToReadyToClose = true
            this.moveToNextStatusButtonText = this.readyToCloseText
            this.moveToNextStatusButtonEnabled = true
            return
          }

          // are all tasks completed for required before change is fully in place with some open actions and we are in being Implemented form status
          if (
            this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
            tasksRequiredBeforeChangeIsFullyInPlace.length === completedTasksRequiredBeforeChangeIsFullyInPlace.length
          ) {
            // these tasks are complete
            this.moc.canMoveToImplemented = true
            this.moveToNextStatusButtonEnabled = true
            this.moveToNextStatusButtonText = this.implementText
            return
          }

          // are all tasks completed for Required before close and the form status is Change is fully in place with some open actions
          if (
            this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
            tasksRequiredBeforeClose.length === completedTasksRequiredBeforeClose.length
          ) {
            // these tasks are complete
            this.moc.canMoveToReadyToClose = true
            this.moveToNextStatusButtonText = this.readyToCloseText
            this.moveToNextStatusButtonEnabled = true
            return
          }
        }
      }

      // someone did not finish their tasks or changed from satisfied to not satisfied
      if (this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value) {
        this.moc.canMoveToImplemented = false
      }

      if (this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value) {
        this.moc.canMoveToReadyToClose = false
      }

      if (
        this.moc.status.value === FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value
      ) {
        this.moc.canMoveToReadyToClose = false
      }

      this.moveToNextStatusButtonEnabled = false
    },
    async makeRevisions(){
      let allGood = false

      if (this.$refs.revisionReasonMo) {
        const result = await this.$refs.revisionReasonMo.validate();
        if (result.valid) {
          allGood = true
        }
      } else {
        allGood = true
      }

      if (allGood) {
        this.moc.revisionReason = { reason: this.revisionReason }
        const data = this.moc
        this.$refs.loadingDialog.show()
        MOCService.save(data, AdvanceType.REVISION)
          .then((result) => {
            if (!result.success) {
              throw new Error(result.message)
            }
            this.$refs.loadingDialog.hide({ redirect: true, message: this.$t('Success_MakeRevisionsSaved') })
          })
          .catch((e) => {
            this.$refs.loadingDialog.error({
              metadata: 'makeRevisions',
              error: e,
              message: this.$t('Error_MakeRevisions')
            })
          })
      }
    },
    saveMoc: async function () {
      const data = this.moc
      return MOCService.save(data, AdvanceType.ADVANCE).then((result) => {
        if (!result.success) {
          throw new Error(result.message)
        }
        return result
      })
    },
    submit: function () {
      if (!this.isSaving) {
        this.isSaving = true
        this.$refs.loadingDialog.show()
        this.saveMoc()
          .then(() => this.$refs.loadingDialog.hide({ redirect: true }))
          .catch((e) => {
            this.$refs.loadingDialog.error({
              metadata: 'submit',
              error: e
            })
          })
          .finally(() => {
            this.isSaving = false
          })
      }
    }
  },
  watch: {    
    tasks:{
      handler(newValue,oldValue){        
        this.canToMoveToNextStatus()        
      },
      deep:true
    }
    
  },
  mounted() {
    EventBus.on('taskStatusChange', () => {
      this.canToMoveToNextStatus()
    })

    this.moveToNextStatusButtonText = this.isAvailableToImplement ? this.implementText : this.readyToCloseText
  }
}
</script>

<style scoped></style>
